<script lang="ts">
import { SelectionFEModelType } from '@/app/common/types/selection.type';
import { ATTACHMENT_OPTIONS } from '@/app/const';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { ComplaintAdditionalDoc, ComplaintDepartmentInfo } from '@/interface/Complaint.interface';
import { userService } from '@/services/serviceProvider';
import { ref } from 'vue';

export default {
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: 't-action-need-more-doc'
        },
        confirmText: {
            type: String,
            default: 't-save'
        },
        cancelText: {
            type: String,
            default: 't-cancel'
        },
        complaintDepartment: {
            type: Object,
            default: {}
        }
    },
    emits: [
        'update:modelValue',
        'onConfirm',
        'onCancel',
        'update:complaint',
        'doRequestAdditionalDocs',
        'displayAlertDialog'
    ],
    setup() {
        const reqDocForm = ref();
        return { reqDocForm };
    },
    data() {
        return {
            careTakers: [] as any[],
            departmentInfo: this.complaintDepartment as ComplaintDepartmentInfo,
            attachmentOptions: [...ATTACHMENT_OPTIONS],
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            },
        }
    },
    watch: {
        dialogShow(val) {
            if (val) {
                this.attachmentOptions = [...ATTACHMENT_OPTIONS];
                this.departmentInfo.additionalDocsModel = [];
                this.attachmentOptions.forEach((o, index) => {
                    const activeAttach = this.departmentInfo.additionalDocs?.find(c => c.docTypeNo === o.value);
                    if (this.departmentInfo.additionalDocsModel) {
                        if (activeAttach) {
                            this.departmentInfo.additionalDocsModel.push({ id: o.value, active: true, remark: activeAttach.remark });
                        } else {
                            this.departmentInfo.additionalDocsModel.push({ id: o.value, active: false });
                        }
                    }
                });
            }
        },
    },
    async mounted() {
    },
    computed: {
        dialogShow: {
            get() {
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        },
        attachmentOther() {
            return this.attachmentOptions.find(o => o.value == 3);
        }
    },
    methods: {
        async confirm() {
            const docsActive = this.departmentInfo.additionalDocsModel?.filter(o => o.active);
            if (docsActive && docsActive.length > 0) {
                const res = this.reqDocForm ? await this.reqDocForm.validate() : true;
                const valid = res.valid === undefined ? true : res.valid;
                if (valid) {
                    this.$emit('onConfirm', {
                        header: this.$t('t-action-need-more-doc'),
                        messageTitle: this.$t('t-action-need-more-doc-confirm'),
                        messageText: undefined,
                        callBack: this.requestAdditionalDocs,
                        callBackCancel: this.reDisplay,
                    } as BaseConfirm)
                }
            } else {
                this.$emit('displayAlertDialog', {
                    header: undefined,
                    messageTitle: this.$t('t-action-need-more-doc-at-least'),
                    messageText: undefined,
                } as BaseAlert)
            }
        },
        reDisplay() {
            this.dialogShow = true;
        },
        cancel() {
            this.dialogShow = false;
            this.departmentInfo.additionalDocs = [];
            this.$emit('update:complaint', this.departmentInfo);
            this.$emit('onCancel');
        },
        attachmentLabel(value: number, remark: string | number | undefined) {
            if (value === 1 || value === 2) {
                return this.$t(`t-${this.attachmentOptions.find(o => o.value === value)?.label}`)
            }
            let otherLabel = this.$t(`t-complaint-req-doc-other`);
            return remark ? `${otherLabel} (${remark})` : otherLabel;
        },
        requestAdditionalDocs() {
            this.dialogShow = false;
            this.departmentInfo.additionalDocs = [];
            if (this.departmentInfo.additionalDocsModel) {
                this.departmentInfo.additionalDocsModel.forEach((o, index) => {
                    if (o.active) {
                        this.departmentInfo.additionalDocs?.push({
                            departmentCode: userService.currentDepartment(),
                            departmentType: this.departmentInfo.departmentType,
                            docTypeNo: o.id,
                            remark: o.remark,
                        } as ComplaintAdditionalDoc)
                    }
                });
            }
            this.$emit('update:complaint', this.departmentInfo);
            this.$emit('doRequestAdditionalDocs');
        },
        onAddClicked() {
            if (this.attachmentOther && this.departmentInfo.additionalDocsModel) {
                let other = { ...this.attachmentOther };
                const orders = this.attachmentOptions.map(s => s.value ?? 0);
                const last = orders && orders.length > 0 ? Math.max(...orders) : 0;
                other.value = last + 1;
                this.attachmentOptions.push(other);
                this.departmentInfo.additionalDocsModel.push({ id: other.value, active: false });
            }
        },
        allActive(): boolean {
            return this.departmentInfo.additionalDocsModel ? this.departmentInfo.additionalDocsModel.filter((o: SelectionFEModelType) => o.id > 2 && o.active === true).length
                === this.departmentInfo.additionalDocsModel.length - 2 : false;
        }
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="80%" :close-on-back="false" persistent>
        <v-form ref="reqDocForm" class="admin-form app-form-input" validate-on="submit lazy" @submit.prevent="confirm">
            <v-card class="dialog-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t(header) }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn icon="mdi-close" @click="cancel"></v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-divider class="mx-4"></v-divider>
                <v-card-text>
                    <div v-if="departmentInfo.additionalDocsModel" v-for="(attach, index) in attachmentOptions"
                        class="complaint-detail">
                        <div class="d-flex border py-2 px-2 mb-2 request-box"
                            :class="departmentInfo.additionalDocsModel[index].active ? 'active' : ''">
                            <div class="d-flex align-center w-50 mr-3"
                                @click="departmentInfo.additionalDocsModel[index].active ? departmentInfo.additionalDocsModel[index].active = false : departmentInfo.additionalDocsModel[index].active = true">
                                <v-checkbox-btn v-model="departmentInfo.additionalDocsModel[index].active"
                                    :label="attachmentLabel(attachmentOptions[index].value, departmentInfo.additionalDocsModel[index].remark)"
                                    class="pe-2"></v-checkbox-btn>
                            </div>
                            <div v-if="attach.value > 2" class="d-flex align-center w-50">
                                <div class="text-label mr-2 mt-1">{{ $t('t-please-fill') }}</div>
                                <v-text-field v-model="departmentInfo.additionalDocsModel[index].remark"
                                    class="hide-float-label" variant="outlined"
                                    :label="$t('t-complaint-search-input-text')"
                                    :disabled="!departmentInfo.additionalDocsModel[index].active" hide-details="auto"
                                    :rules="departmentInfo.additionalDocsModel[index].active ? [rules.required(`${$t('t-action-need-more-doc-detail')}`)] : []">
                                </v-text-field>
                            </div>
                            <div v-else class="d-flex align-center w-50">
                                <div class="text-label mr-2">{{ $t('t-remark') }}</div>
                                <v-text-field v-model="departmentInfo.additionalDocsModel[index].remark"
                                    class="hide-float-label" variant="outlined"
                                    :label="$t('t-action-need-more-doc-remark')"
                                    :disabled="!departmentInfo.additionalDocsModel[index].active" hide-details="auto">
                                </v-text-field>
                            </div>
                        </div>
                    </div>
                    <div class="w-100 border border-dashed d-flex align-center justify-center mt-6 mb-4 py-3 add-sub"
                        v-if="allActive()" @click="onAddClicked()">
                        <v-icon icon="mdi mdi-plus mr-1"></v-icon>{{ $t('t-complaint-additional-docs-add') }}
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-container class="mx-0">
                        <div class="d-flex justify-end">
                            <v-btn @click="cancel" variant="outlined" color="primary" size="large">
                                {{ $t(cancelText) }}
                            </v-btn>
                            <v-btn @click="confirm" variant="flat" color="primary" size="large">
                                {{ $t(confirmText) }}
                            </v-btn>
                        </div>
                    </v-container>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>